import { useRef, useState } from 'react';
import { Alert, Button } from '@yhelp/ui';

const ACCEPT =
  'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,.jpg,.jpeg,.png,.webp,.gif,.mp4,.webm';

const MAX_IMAGE = 5 * 1024 * 1024;
const MAX_VIDEO = 25 * 1024 * 1024;
const MAX_FILES = 5;

export interface PendingAttachment {
  file: File;
  previewUrl?: string;
}

function validateClientFile(file: File): string | null {
  const allowed = ACCEPT.split(',');
  const ext = file.name.includes('.') ? `.${file.name.split('.').pop()?.toLowerCase()}` : '';
  if (!allowed.includes(file.type) && !allowed.includes(ext)) {
    return 'Tipo não permitido. Use JPEG, PNG, WebP, GIF, MP4 ou WebM.';
  }
  const isVideo = file.type.startsWith('video/') || ext === '.mp4' || ext === '.webm';
  const max = isVideo ? MAX_VIDEO : MAX_IMAGE;
  if (file.size > max) {
    return isVideo ? 'Vídeos devem ter no máximo 25 MB.' : 'Imagens devem ter no máximo 5 MB.';
  }
  return null;
}

export function TicketAttachmentField({
  files,
  onChange,
  disabled,
}: {
  files: PendingAttachment[];
  onChange: (files: PendingAttachment[]) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    setError(null);
    const next = [...files];
    for (const file of Array.from(list)) {
      if (next.length >= MAX_FILES) {
        setError('Máximo de 5 anexos por chamado.');
        break;
      }
      const err = validateClientFile(file);
      if (err) {
        setError(err);
        continue;
      }
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      next.push({ file, previewUrl });
    }
    onChange(next);
  };

  const removeAt = (index: number) => {
    const removed = files[index];
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div data-tour="ticket-attachments">
      <label className="block text-sm font-medium text-yhelp-black dark:text-white">
        Anexos (imagens ou vídeos)
      </label>
      <p className="mt-1 text-xs text-muted">
        Até 5 arquivos — imagens até 5 MB, vídeos até 25 MB (JPEG, PNG, WebP, GIF, MP4, WebM).
      </p>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="sr-only"
        disabled={disabled || files.length >= MAX_FILES}
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="mt-2"
        disabled={disabled || files.length >= MAX_FILES}
        onClick={() => inputRef.current?.click()}
      >
        Adicionar anexo
      </Button>
      {error ? (
        <Alert variant="error" title="Anexo inválido" className="mt-3">
          {error}
        </Alert>
      ) : null}
      {files.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {files.map((item, i) => (
            <li
              key={`${item.file.name}-${i}`}
              className="flex items-center gap-3 rounded-lg border border-neutral-200 p-2 dark:border-neutral-700"
            >
              {item.previewUrl ? (
                <img src={item.previewUrl} alt="" className="h-12 w-12 rounded object-cover" />
              ) : (
                <span className="flex h-12 w-12 items-center justify-center rounded bg-neutral-100 text-xs dark:bg-neutral-800">
                  vídeo
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{item.file.name}</p>
                <p className="text-xs text-muted">{(item.file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <Button type="button" size="sm" variant="ghost" onClick={() => removeAt(i)}>
                Remover
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export async function uploadTicketAttachments(
  ticketId: string,
  files: PendingAttachment[],
  authorizedFetch: (url: string, init?: RequestInit) => Promise<unknown>,
): Promise<void> {
  for (const { file } of files) {
    const body = new FormData();
    body.append('file', file);
    await authorizedFetch(`/v1/tickets/${ticketId}/attachments`, {
      method: 'POST',
      body,
    });
  }
}
